import type { SaveData } from "../core/SaveManager";
import type { RunMetrics } from "../engine/RunSession";
import { checkAchievements, type Unlock } from "./Achievements";
import { claimSeason, collectLetters, wordHuntReward } from "./Hunts";
import { applyRun, missionText, type MetaEvents, type MissionDefinition, MAX_MULTIPLIER_SETS } from "./Missions";
import { applyReward, rewardText, type BoxReward } from "./Shop";

export interface RunSummary {
  missionsCompleted: string[];
  setCompleted: boolean;
  newMultiplier: number | null;
  superBox: boolean;
  highScore: boolean;
  wordHunt: { completed: boolean; streak: number; reward: string | null };
  seasonRewards: string[];
  achievements: Unlock[];
}

/** Applies a finished run to the save: missions and the multiplier, hunts, achievements, lifetime stats, boards and keys. */
export function applyRunToSave(save: SaveData, run: RunMetrics, highScore: boolean, meta: Partial<MetaEvents> = {}): RunSummary {
  // the daily word and the season
  const hunt = collectLetters(save.wordHunt, run.letters);
  let huntReward: string | null = null;
  if (hunt.completed) {
    const reward: BoxReward = wordHuntReward(hunt.streak);
    if (hunt.streak >= 5) { save.inventory.superBoxes += 1; huntReward = "SUPER MYSTERY BOX"; }
    else { applyReward(save, reward); huntReward = rewardText(reward); }
  }
  save.seasonHunt.tokens += run.tokens;
  const seasonRewards = claimSeason(save.seasonHunt).map((reward) => { applyReward(save, reward); return rewardText(reward); });

  const shop = save.pendingMeta ?? { spentCoins: 0, boughtBoxes: 0 };
  const events: MetaEvents = { spentCoins: shop.spentCoins, boughtBoxes: shop.boughtBoxes, wordHuntsCompleted: hunt.completed ? 1 : 0, highScoreBeaten: highScore, ...meta };
  save.pendingMeta = { spentCoins: 0, boughtBoxes: 0 };
  const result = applyRun(save.missions, run, events);
  let newMultiplier: number | null = null;
  if (result.setCompleted) {
    if (save.missions.setIndex <= MAX_MULTIPLIER_SETS) { save.multiplier = Math.min(MAX_MULTIPLIER_SETS, save.multiplier + 1); newMultiplier = save.multiplier; }
    if (result.superBox) save.inventory.superBoxes += 1;
  }
  save.stats.missionsDone += result.completed.length;
  save.stats.rides += run.rides;
  save.stats.escapes += run.escapes;
  save.stats.boardsUsed += run.boardsUsed;
  save.stats.jumps += run.jumps;
  save.stats.dodges += run.dodges;
  save.inventory.mysteryBoxes += run.boxes;
  const unlocks = checkAchievements(save);
  return {
    missionsCompleted: result.completed.map((d: MissionDefinition) => missionText(d)), setCompleted: result.setCompleted, newMultiplier,
    superBox: result.superBox, highScore, wordHunt: { completed: hunt.completed, streak: hunt.streak, reward: huntReward }, seasonRewards, achievements: unlocks
  };
}
